import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlaceIcon from "@mui/icons-material/Place";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { Link as RouterLink, useNavigate } from "react-router";
import { useState, type FormEvent } from "react";
import {
  useContentList,
  useDirtTalkToday,
  useRigOfWeek,
} from "../hooks/useApiQueries.js";
import { useAuth } from "../hooks/useAuth.js";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed.js";
import { ContentCard } from "../components/ContentCard.js";
import { DirtTalkCard } from "../components/DirtTalkCard.js";
import { HeroVideoCarousel } from "../components/HeroVideoCarousel.js";
import { VideoLightbox } from "../components/VideoLightbox.js";
import { useVideoLightbox } from "../hooks/useVideoLightbox.js";
import { SEO } from "../components/SEO.js";
import { NEON_GLOW, NEON_GLOW_SOFT, NeonEyebrow, neonButtonSx } from "../components/Neon.js";
import { proxied, isLikelyImageUrl } from "../lib/imageProxy.js";

const SEEDED_POSTCODES = [
  { postcode: "3575", suburb: "Pyramid Hill" },
  { postcode: "3559", suburb: "Mitiamo" },
  { postcode: "3556", suburb: "Bridgewater" },
  { postcode: "3517", suburb: "Inglewood" },
  { postcode: "3527", suburb: "Wedderburn" },
  { postcode: "3525", suburb: "Boort" },
];

export function Home() {
  // Pull a few extra so the hero has rotation material AND we can display
  // the "Fresh from the field" grid below from the same fetch.
  const { data: heroVideosRes } = useContentList({ type: "VIDEO", pageSize: 5 });
  const { data: contentRes, isLoading: contentLoading } = useContentList({ pageSize: 8 });
  const { data: articlesRes } = useContentList({ type: "ARTICLE", pageSize: 4 });
  const { data: dirtTalk, isLoading: dirtLoading } = useDirtTalkToday();
  const { data: rig } = useRigOfWeek();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [postcodeInput, setPostcodeInput] = useState("");
  const { openFor: openLightbox, lightboxProps } = useVideoLightbox();
  const { items: recentItems, clear: clearRecent } = useRecentlyViewed();

  const onPostcodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    const cleaned = postcodeInput.trim();
    if (/^\d{4}$/.test(cleaned)) navigate(`/built-near-you/${cleaned}`);
  };

  return (
    <>
      <SEO
        title="Earthmoving content, curated by the Corian Park team"
        description="Curated Australian earthmoving and mining videos and stories. Daily Dirt Talk, weekly Rig of the Week, and content from your patch."
        canonical="https://corianpark.au/"
      />

      {/* === HERO: cycling video carousel === */}
      <HeroVideoCarousel videos={heroVideosRes?.data ?? []} />

      {/* === VALUE STATEMENT === */}
      <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: "center", maxWidth: 760, mx: "auto", px: 2 }}>
        <Typography
          variant="h3"
          sx={{
            fontSize: { xs: 22, md: 30 },
            fontWeight: 700,
            lineHeight: 1.35,
            color: "text.primary",
          }}
        >
          Curated content by the Corian Park team, so you don't have to go looking.
        </Typography>
      </Box>

      {/* === MEMBERSHIP HOOK === */}
      {!user && (
        <Box
          sx={{
            mb: { xs: 6, md: 9 },
            mx: { xs: -2, sm: -3 },
            py: { xs: 4, md: 5 },
            px: 3,
            textAlign: "center",
            background:
              "linear-gradient(135deg, rgba(255,176,0,0.10) 0%, rgba(255,136,0,0.04) 60%, transparent 100%)",
            borderTop: "1px solid",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <NeonEyebrow glow>YOUR LOADER BUCKET</NeonEyebrow>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: 26, md: 36 },
              fontWeight: 900,
              mt: 1.5,
              mb: 1,
              letterSpacing: "-0.01em",
            }}
          >
            Create a profile and get straight to the machinery you care about.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 540, mx: "auto", mb: 3 }}>
            Save the videos, articles and rigs you actually want to come back to.
          </Typography>
          <Button
            component={RouterLink}
            to="/auth/sign-in"
            variant="contained"
            color="primary"
            size="large"
            startIcon={<BookmarkBorderIcon />}
            sx={{ ...neonButtonSx, fontSize: 16, px: 4 }}
          >
            Sign up free
          </Button>
        </Box>
      )}

      {/* === RECENTLY VIEWED === */}
      {recentItems.length > 0 && (
        <Box sx={{ mb: { xs: 5, md: 8 } }}>
          <Stack direction="row" sx={{ alignItems: "flex-end", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <NeonEyebrow glow>FOR YOU</NeonEyebrow>
              <Typography variant="h3" sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 800, mt: 1, lineHeight: 1.1 }}>
                Pick up where you left off
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={clearRecent}
              sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}
            >
              Clear
            </Button>
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              overflowX: "auto",
              pb: 1,
              // Hide scrollbar — touch + scroll-snap covers mobile, desktop
              // gets the chunkier whitespace as a swipe affordance.
              scrollbarWidth: "thin",
              scrollSnapType: "x mandatory",
              "& > a": { scrollSnapAlign: "start" },
            }}
          >
            {recentItems.map((item) => (
              <Box
                key={item.slug}
                component={RouterLink}
                to={`/content/${item.slug}`}
                sx={{
                  display: "block",
                  flexShrink: 0,
                  width: { xs: 220, md: 260 },
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: NEON_GLOW_SOFT,
                    borderColor: "primary.main",
                  },
                }}
              >
                {isLikelyImageUrl(item.thumbnailUrl) ? (
                  <Box
                    component="img"
                    src={proxied(item.thumbnailUrl!)}
                    alt=""
                    loading="lazy"
                    sx={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <Box sx={{ width: "100%", aspectRatio: "16 / 9", bgcolor: "action.hover" }} />
                )}
                <Box sx={{ p: 1.25 }}>
                  <Typography
                    variant="caption"
                    color="primary.main"
                    sx={{ fontWeight: 700, letterSpacing: 1, fontSize: 10 }}
                  >
                    {item.type === "VIDEO" ? "VIDEO" : "ARTICLE"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      mt: 0.25,
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* === LATEST VIDEOS === */}
      <Box sx={{ mb: { xs: 6, md: 10 } }} data-testid="content-feed-section">
        <Stack direction="row" sx={{ alignItems: "flex-end", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <NeonEyebrow glow>LATEST</NeonEyebrow>
            <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 900, mt: 1, lineHeight: 1.1 }}>
              Fresh from the field
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="/watch"
            endIcon={<ArrowForwardIcon />}
            sx={{ color: "primary.main", fontWeight: 700 }}
          >
            All videos
          </Button>
        </Stack>
        {contentLoading && (
          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        )}
        {!contentLoading && contentRes?.data.length === 0 && (
          <Alert severity="info">No content yet. The trawler is feeding — check back soon.</Alert>
        )}
        <Grid container spacing={2}>
          {contentRes?.data.map((item) => (
            <Grid key={item.id} size={{ xs: 12, sm: 6, md: 3 }}>
              <ContentCard item={item} showSaveButton onVideoClick={openLightbox} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* === DIRT TALK + RIG OF THE WEEK === */}
      {/* Only render the Dirt Talk column while loading or when a card exists —
          an empty "No card today" placeholder reads as unfinished. When hidden,
          Rig of the Week flexes to full width. */}
      <Box sx={{ mb: { xs: 6, md: 10 } }} data-testid="dirt-talk-section">
        <Grid container spacing={3}>
          {(dirtLoading || dirtTalk?.items[0]) && (
            <Grid size={{ xs: 12, md: 6 }}>
              <NeonEyebrow glow sx={{ display: "block", mb: 1 }}>DIRT TALK · TODAY</NeonEyebrow>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 480 }}>
                Every day — the machine everyone's talking about, a fact that'll surprise you, and a
                moment from Australian earthmoving history.
              </Typography>
              {dirtLoading ? (
                <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
              ) : (
                dirtTalk?.items[0] && (
                  <RouterLink to={`/dirt-talk/${dirtTalk.date}`} style={{ textDecoration: "none", color: "inherit" }}>
                    <Box
                      sx={{
                        transition: "transform 200ms ease, box-shadow 200ms ease",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: NEON_GLOW_SOFT },
                      }}
                    >
                      <DirtTalkCard
                        variant={dirtTalk.items[0].variant}
                        date={dirtTalk.date}
                        title={dirtTalk.items[0].title}
                        body={dirtTalk.items[0].body}
                        imageUrl={dirtTalk.items[0].imageUrl}
                        itemId={dirtTalk.items[0].id}
                        showSaveButton
                      />
                    </Box>
                  </RouterLink>
                )
              )}
            </Grid>
          )}

          <Grid size={{ xs: 12, md: dirtLoading || dirtTalk?.items[0] ? 6 : 12 }}>
            <NeonEyebrow glow sx={{ display: "block", mb: 1 }}>RIG OF THE WEEK</NeonEyebrow>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 480 }}>
              The single rig that earned the spotlight this week. Real specs, real operators.
            </Typography>
            {rig ? (
              <RouterLink to="/rig-of-the-week" style={{ textDecoration: "none", color: "inherit" }}>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: NEON_GLOW,
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {isLikelyImageUrl(rig.equipment?.machinery?.primaryImageUrl ?? rig.imageUrl) && (
                    <Box sx={{ position: "relative", aspectRatio: "16 / 10" }}>
                      <Box
                        component="img"
                        src={proxied((rig.equipment?.machinery?.primaryImageUrl ?? rig.imageUrl)!)}
                        alt={rig.equipment?.machinery?.name ?? "Rig of the Week"}
                        loading="lazy"
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background: "linear-gradient(180deg, rgba(0,0,0,0.0) 50%, rgba(0,0,0,0.8) 100%)",
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{ p: 2.5 }}>
                    {rig.equipment?.machinery?.brand && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        {rig.equipment.machinery.brand.name}
                      </Typography>
                    )}
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1, mb: 1 }}>
                      {rig.equipment?.machinery?.name ?? "This Week's Rig"}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "primary.main", fontWeight: 700 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>See the breakdown</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </Stack>
                  </Box>
                </Box>
              </RouterLink>
            ) : (
              <Box
                sx={{
                  p: 4,
                  borderRadius: 1,
                  border: "1px dashed",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  minHeight: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">Coming Monday.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* === FROM YOUR PATCH (postcode discovery, content-led) === */}
      <Box sx={{ mb: { xs: 6, md: 10 } }}>
        <NeonEyebrow glow sx={{ display: "block", mb: 1 }}>FROM YOUR PATCH</NeonEyebrow>
        <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 900, mt: 1, mb: 1, lineHeight: 1.1 }}>
          See what's been filmed near you
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
          Drop your postcode — we'll surface the machines, operators and stories from your area.
        </Typography>
        <Box
          component="form"
          onSubmit={onPostcodeSubmit}
          sx={{ display: "flex", gap: 1, mb: 2, maxWidth: 360 }}
        >
          <TextField
            size="small"
            placeholder="e.g. 3525"
            value={postcodeInput}
            onChange={(e) => setPostcodeInput(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
            slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]{4}", maxLength: 4 } }}
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!/^\d{4}$/.test(postcodeInput.trim())}
            sx={{ ...neonButtonSx, flexShrink: 0 }}
          >
            Go
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Loddon Valley shortcuts:
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
          {SEEDED_POSTCODES.map((p) => (
            <Chip
              key={p.postcode}
              component={RouterLink}
              to={`/built-near-you/${p.postcode}`}
              icon={<PlaceIcon sx={{ fontSize: 14 }} />}
              label={`${p.suburb} · ${p.postcode}`}
              size="small"
              clickable
              sx={{
                bgcolor: "background.paper",
                "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* === LATEST FROM READ === */}
      {articlesRes && articlesRes.data.length > 0 && (
        <Box sx={{ mb: { xs: 6, md: 10 } }}>
          <Stack direction="row" sx={{ alignItems: "flex-end", justifyContent: "space-between", mb: 3 }}>
            <Box>
              <NeonEyebrow glow>READ</NeonEyebrow>
              <Typography variant="h2" sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 900, mt: 1, lineHeight: 1.1 }}>
                Words from the field
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/read"
              endIcon={<ArrowForwardIcon />}
              sx={{ color: "primary.main", fontWeight: 700 }}
            >
              All articles
            </Button>
          </Stack>
          <Grid container spacing={2}>
            {articlesRes.data.map((item) => (
              <Grid key={item.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <ContentCard item={item} showSaveButton onVideoClick={openLightbox} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* === CORIAN PARK ON FACEBOOK === */}
      <Box sx={{ mb: { xs: 6, md: 10 } }}>
        <Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
          <Grid size={{ xs: 12, md: 5 }}>
            <NeonEyebrow glow>FOLLOW US</NeonEyebrow>
            <Typography
              variant="h2"
              sx={{ fontSize: { xs: 28, md: 36 }, fontWeight: 900, mt: 1, mb: 2, lineHeight: 1.1 }}
            >
              Corian Park on Facebook
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 420 }}>
              The Corian Park team on the ground — job site shots, machine deliveries, and the
              stuff that doesn't make it into a press release.
            </Typography>
            <Button
              component="a"
              href="https://www.facebook.com/CorianParkEarthmoving/"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderColor: "primary.main", color: "primary.main", fontWeight: 700 }}
            >
              Open on Facebook
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box
              sx={{
                borderRadius: 1,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                lineHeight: 0,
                "& iframe": { display: "block", maxWidth: "100%" },
              }}
            >
              <Box
                component="iframe"
                src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FCorianParkEarthmoving%2F&tabs=timeline&width=500&height=600&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false&colorscheme=dark"
                width="500"
                height="600"
                scrolling="no"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                sx={{ border: 0, width: "100%", maxWidth: 500, height: 600, display: "block" }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <VideoLightbox {...lightboxProps} />

      {/* === FOOTER LINE === */}
      <Box sx={{ mt: 8, py: 5, textAlign: "center", borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="body1" sx={{ color: "text.secondary", fontStyle: "italic", mb: 1.5 }}>
          Built by the Corian Park team, who love machines as much as you.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <RouterLink to="/quote" style={{ color: "inherit" }}>
            Need a quote? →
          </RouterLink>
        </Typography>
      </Box>
    </>
  );
}
